/**
 * SEO — shared canonical metadata mechanism.
 *
 * PUBLIC-SEO-LAUNCH-GAP-REMEDIATION (2026-02):
 *   - One and only one head-mutation authority for public pages.
 *   - Derives values from the caller (which must derive from canonical
 *     SSOTs — treatment_content, clinic_profiles, legal_entities,
 *     platform_content). This component never fetches, never fabricates,
 *     never introduces a parallel SEO CMS.
 *   - Uses react-helmet-async which is React-lifecycle safe and replaces
 *     the imperative document.head manipulation on TreatmentDetailPage.
 *
 * PROPS (all optional except where noted):
 *   - title         : full <title> (falls back to "TreatX")
 *   - description   : meta description + og/twitter description
 *   - canonicalPath : path relative to origin (e.g. "/treatments/dental-implants")
 *                     rendered as an ABSOLUTE canonical link. Because the
 *                     shell is a HashRouter SPA we always resolve against
 *                     window.location.origin.
 *   - image         : absolute or relative image URL for og:image/twitter:image
 *   - imageAlt      : accessible alt for the OG image
 *   - type          : og:type (default "website")
 *   - keywords      : optional string for meta[name="keywords"] (deprecated
 *                     for Google but still emitted for parity with legacy)
 *   - noindex       : if true, emits robots="noindex, follow" — used on
 *                     private/search surfaces and on 404 UIs so Google
 *                     doesn't keep the URL in its index.
 *   - jsonLd        : optional JSON-LD object; will be serialised into a
 *                     single <script type="application/ld+json"> block.
 *   - locale        : og:locale (default "en_US"). Callers using
 *                     react-i18next can pass i18n.language.
 *   - lang          : optional bare language code ("en", "tr") written to
 *                     <html lang> so the served language and the declared
 *                     language cannot disagree.
 *   - statusCode    : optional HTTP status the PRERENDER layer should return
 *                     for this render (404 for an unknown page, 503 while a
 *                     dependency is down). Emitted as the industry-standard
 *                     `<meta name="prerender-status-code">`, which headless
 *                     prerenderers (Prerender.io, Rendertron and the
 *                     crawler-cache in front of Production) read to turn a
 *                     client-rendered error page into a real HTTP status.
 *                     A browser ignores it; see TREATX-SEO-RECOVERY-S3A.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'TreatX';
const DEFAULT_DESCRIPTION =
  'TreatX — Compare verified clinics, get personalized quotes, and save on medical treatments worldwide.';
const DEFAULT_IMAGE = '/favicon.svg';

function resolveOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return 'https://treatx.health';
}

// TREATX-L1F: canonical global / TR host pair. Consumed by SEO to
// emit correct `<link rel="alternate" hreflang>` metadata across the
// two logical surfaces without requiring the two hosts to be up.
const GLOBAL_HOST = 'treatx.health';
const TR_HOST = 'tr.treatx.health';

// TREATX-SEO-RECOVERY-S1 (2026-06): the TR surface host is NOT serving.
// `https://tr.treatx.health/` resolves in DNS but answers nothing, so every
// page was publishing a `hreflang="tr"` alternate pointing at a dead URL —
// an invalid cluster that search engines discard, taking the `en` and
// `x-default` annotations in the same cluster with it. While exactly one host
// serves the site there is nothing to annotate, so no alternates are emitted.
// Flip this to `true` in the SAME commit that brings `tr.treatx.health` up;
// the annotation logic below is unchanged and starts working again.
const TR_SURFACE_SERVING = false;

function surfacePeers(canonicalPath) {
  if (!canonicalPath || !TR_SURFACE_SERVING) return null;
  const path = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  return {
    en: `https://${GLOBAL_HOST}${path}`,
    tr: `https://${TR_HOST}${path}`,
  };
}

function absolutise(pathOrUrl) {
  if (!pathOrUrl) return '';
  // TREATX-SEO-RECOVERY-S3B — the head is shared by every page, so a caller
  // passing something that is not a URL must not be able to throw here and
  // unmount the whole tree (that is precisely how a media-descriptor object
  // blanked the provider profile). A non-string can never be a URL, so the
  // metadata is omitted rather than fabricated. Callers resolve descriptors
  // to strings at their own canonical boundary; see resolveClinicLogoUrl.
  if (typeof pathOrUrl !== 'string') return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const origin = resolveOrigin();
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${origin}${path}`;
}

const SEO = ({
  title,
  description,
  canonicalPath,
  image,
  imageAlt,
  type = 'website',
  keywords,
  noindex = false,
  jsonLd,
  locale = 'en_US',
  lang,
  statusCode,
}) => {
  const trimmed = (title || '').trim();
  const fullTitle = trimmed && trimmed !== SITE_NAME ? `${trimmed} | ${SITE_NAME}` : SITE_NAME;
  const desc = description || DEFAULT_DESCRIPTION;
  const canonicalUrl = canonicalPath ? absolutise(canonicalPath) : undefined;
  const img = absolutise(image || DEFAULT_IMAGE);

  return (
    <Helmet prioritizeSeoTags {...(lang ? { htmlAttributes: { lang } } : {})}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
      {/* TREATX-SEO-RECOVERY-S3A — tells the prerender layer which HTTP status
          this client-rendered page really represents. Harmless everywhere
          else: browsers and React ignore an unknown meta name. */}
      {statusCode ? (
        <meta name="prerender-status-code" content={String(statusCode)} />
      ) : null}
      {/* TREATX-L1F: hreflang alternates for the two public surfaces.
          Emitted as flat children (react-helmet-async does not descend
          into fragments). Only emitted for public pages with a canonical
          path; private pages (noindex) omit alternates. */}
      {canonicalUrl && !noindex && surfacePeers(canonicalPath) && (
        <link rel="alternate" hrefLang="en" href={surfacePeers(canonicalPath).en} />
      )}
      {canonicalUrl && !noindex && surfacePeers(canonicalPath) && (
        <link rel="alternate" hrefLang="tr" href={surfacePeers(canonicalPath).tr} />
      )}
      {canonicalUrl && !noindex && surfacePeers(canonicalPath) && (
        <link rel="alternate" hrefLang="x-default" href={surfacePeers(canonicalPath).en} />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:image" content={img} />
      {imageAlt ? <meta property="og:image:alt" content={imageAlt} /> : null}
      <meta property="og:locale" content={locale} />

      {/* Twitter / X */}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {imageAlt ? <meta name="twitter:image:alt" content={imageAlt} /> : null}

      {/* Optional structured data — one canonical JSON-LD block per page. */}
      {jsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      ) : null}
    </Helmet>
  );
};

export default SEO;
